import {
	GraphQLResultsTestRunHistoryBySuiteOutput,
	GraphQLResultsTestRunHistoryBySpecificationOutput,
	GraphQLResultsTestRunHistoryByTestCaseOutput,
} from 'src/app/graphql/models/output/results-test-run-history.output.model';
import { TestHistoryEntry } from 'src/app/models/test-history.model';

/**
 * Newest execution first (the list may not be ordered; the card reverses for display).
 * @param entries
 */
function sortNewestFirst(entries: TestHistoryEntry[]): TestHistoryEntry[] {
	return entries.sort((a, b) => (b.executionTime ?? '').localeCompare(a.executionTime ?? ''));
}

/**
 * Maps Results test runs for a test suite into TestHistoryEntry[] for the shared
 * test-history card. The Results list has no per-run status or sub-suite counts,
 * so subSuites is empty and the card is fed with showStatus=false.
 * Returned newest-execution-first (the card reverses for display).
 * @param runs
 */
export function mapGraphQLResultsTestRunsToHistoryEntries(runs: GraphQLResultsTestRunHistoryBySuiteOutput[]): TestHistoryEntry[] {
	const entries: TestHistoryEntry[] = runs.map(run => ({
		uid: run.testRun.testRunUid,
		testSuiteName: run.testSuite?.name ?? null,
		executionTime: run.testRun.executionTime ?? null,
		analyzerVehicleConfiguration: run.testRun.environment ?? null,
		classification: null,
		environment: run.testRun.environment ?? null,
		regBy: run.testRun.regBy ?? null,
		// testSuiteVersion is the version of the calling suite; testSuite.* is the run's own suite.
		testSuiteVersion: run.testSuiteVersion ?? null,
		mainTestSuiteName: run.testSuite?.name ?? null,
		mainTestSuiteVersion: run.testSuite?.version ?? null,
		subSuites: [],
	}));
	return sortNewestFirst(entries);
}

/** testRunType discriminator: 410 = the run was a test specification, otherwise a test suite. */
const SPECIFICATION_RUN_TYPE = 410;

/**
 * Maps Results test runs for a single TEST CASE into TestHistoryEntry[] for the shared card's
 * "results for this test case" table. Each run wraps the suite/spec that ran the case (itemNo /
 * name / type) plus the case's own result in that run (from testResults[0]). Mirrors the Results
 * client's test-case results page. Returned newest-execution-first (the card reverses for display).
 * @param runs
 */
export function mapGraphQLResultsTestRunsForTestCaseToHistoryEntries(runs: GraphQLResultsTestRunHistoryByTestCaseOutput[]): TestHistoryEntry[] {
	const entries: TestHistoryEntry[] = runs.map(run => {
		const isSpec = run.testRunType === SPECIFICATION_RUN_TYPE;
		const container = isSpec ? run.testSpecification : run.testSuite;
		// The case's result for this run lives on the first (and typically only) test result.
		const testResult = run.testRun.testResults?.[0];
		return {
			uid: run.testRun.testRunUid,
			testSuiteName: container?.name ?? null,
			executionTime: run.testRun.executionTime ?? null,
			analyzerVehicleConfiguration: run.testRun.environment ?? null,
			classification: null,
			environment: run.testRun.environment ?? null,
			regBy: run.testRun.regBy ?? null,
			// For the test-case view "version" is the test case version, not the suite version.
			testSuiteVersion: testResult?.testCaseVersion ?? null,
			itemNo: container?.itemNo ?? null,
			name: container?.name ?? null,
			typeLabel: isSpec ? 'Test specification' : 'Test suite',
			result: testResult?.result ?? null,
			resultComment: testResult?.resultComment ?? null,
			troubleReportCount: testResult?.troubleReports?.length ?? 0,
			testResultUid: testResult?.testResultUid ?? null,
			subSuites: [],
		};
	});
	return sortNewestFirst(entries);
}

/**
 * Specification counterpart of mapGraphQLResultsTestRunsToHistoryEntries. Runs from the
 * resultsTestRunModelsByTestSpecification query carry a testSpecification ref instead of a
 * testSuite and have no testSuiteVersion, so the specification's name/version feed the name
 * columns and the suite-specific fields stay null.
 * @param runs
 */
export function mapGraphQLResultsTestSpecRunsToHistoryEntries(runs: GraphQLResultsTestRunHistoryBySpecificationOutput[]): TestHistoryEntry[] {
	const entries: TestHistoryEntry[] = runs.map(run => ({
		uid: run.testRun.testRunUid,
		testSuiteName: run.testSpecification?.name ?? null,
		executionTime: run.testRun.executionTime ?? null,
		analyzerVehicleConfiguration: run.testRun.environment ?? null,
		classification: null,
		environment: run.testRun.environment ?? null,
		regBy: run.testRun.regBy ?? null,
		testSuiteVersion: run.testSpecification?.version ?? null,
		mainTestSuiteName: run.testSpecification?.name ?? null,
		mainTestSuiteVersion: run.testSpecification?.version ?? null,
		subSuites: [],
	}));
	return sortNewestFirst(entries);
}
